import { format, isAfter, isBefore, parseISO } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { capitalizeFirstLetter } from '../Util';
import formatCloseRelative from '../Util/formatCloseRelative';
import { type ScheduleAccordionProps } from './Situation.types';

declare const bootstrap: typeof import('bootstrap');

export const ScheduleAccordion: React.FC<ScheduleAccordionProps> = (props) => {
    const listRef = useRef<HTMLUListElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const listElement = listRef.current;
        if (listElement) {
            const handleShow = () => setIsExpanded(true);
            const handleHide = () => setIsExpanded(false);

            listElement.addEventListener('show.bs.collapse', handleShow);
            listElement.addEventListener('hide.bs.collapse', handleHide);

            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('scheduleExpanded') != null) {
                if (bootstrap.Collapse) {
                    const bsCollapse = new bootstrap.Collapse(listElement);
                    bsCollapse.show();
                }
            }

            return () => {
                listElement.removeEventListener('show.bs.collapse', handleShow);
                listElement.removeEventListener('hide.bs.collapse', handleHide);
            };
        }
    }, []);
    const now = new Date();
    const isStarted = isBefore(parseISO(props.timeSpans[0].startTime), now);
    const currentAndFuture = props.timeSpans.filter((ts) => isAfter(parseISO(ts.endTime), now));
    if (currentAndFuture.length === 0) {
        return null;
    }
    const currentOrNext = currentAndFuture[0];
    const isActive = isBefore(parseISO(currentOrNext.startTime), now);
    const future = currentAndFuture.slice(isActive ? 1 : 0);

    const getStatusText = () => {
        if (!isStarted) return 'Ej startat';
        if (isActive) return 'Aktivt';
        return 'Ej aktivt';
    };

    return (
        <div className={`schedule-accordion${props.inline ? ' schedule-accordion--inline' : ''}`}>
            <div className="schedule-accordion__heading">
                <Icon className="schedule-accordion__icon" symbol="recurringEvent" />
                <span>Återkommande händelse</span>
            </div>
            <div className="schedule-accordion__current d-flex">
                <div className="schedule-accordion__current-status">
                    <div>Aktuell status: </div>
                    <div>Starttid: </div>
                    <div>Sluttid: </div>
                </div>
                <div className="schedule-accordion__current-values">
                    <div>{getStatusText()}</div>
                    <div>{capitalizeFirstLetter(formatCloseRelative(parseISO(currentOrNext.startTime)))}</div>
                    <div>{capitalizeFirstLetter(formatCloseRelative(parseISO(currentOrNext.endTime)))}</div>
                </div>
            </div>

            {currentAndFuture.length > 1 &&
                (props.detailsUrl ? (
                    <a className="schedule-accordion__link" href={`${props.detailsUrl}?scheduleExpanded`}>
                        {`Visa alla planerade tillfällen (${future.length})`}
                    </a>
                ) : (
                    <>
                        <div className="schedule-accordion__planned">
                            <button
                                className="btn unstyled-button"
                                onMouseDown={(e) => e.preventDefault()} // Hide outline on mouse click
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseOne"
                                aria-expanded={isExpanded ? 'true' : 'false'}
                                aria-controls="collapseOne"
                            >
                                {`${isExpanded ? 'Dölj planerade' : 'Visa alla planerade'} (${future.length})`}
                                <i
                                    className={`schedule-accordion__planned-button-icon icon-up-open ${
                                        !isExpanded && 'schedule-accordion__planned-button-icon--rotated'
                                    }`}
                                />
                            </button>
                        </div>
                        <ul ref={listRef} id="collapseOne" className={'schedule-accordion__planned-list list-unstyled collapse'}>
                            {future.map((ts, index) => (
                                <li
                                    key={`${ts.startTime}-${ts.endTime}`}
                                    className={index === 0 ? 'schedule-accordion__planned-list-item--current' : ''}
                                >
                                    {capitalizeFirstLetter(formatCloseRelative(parseISO(ts.startTime), 'includeTime'))}
                                    {format(parseISO(ts.startTime), 'yyyy-MM-dd') === format(parseISO(ts.endTime), 'yyyy-MM-dd')
                                        ? '-' + format(parseISO(ts.endTime), 'HH.mm')
                                        : ' till ' + formatCloseRelative(parseISO(ts.endTime), 'includeTime')}
                                </li>
                            ))}
                        </ul>
                    </>
                ))}
        </div>
    );
};

export default ScheduleAccordion;
